// Step 6 of the client-side X12 pipeline: checks the envelope structure and,
// for each 850 or 852, its loop content against the dictionary — and returns
// a flat list of advisory findings. This module is advisory-only *by
// construction*: nothing in its public API returns a boolean "can export"
// gate, so nothing downstream can accidentally block export on a finding it
// produces.

import type {EdiElementValue, EdiSegment} from './tokenizer';
import {getSegmentDefinition} from './dictionary';
import type {ItemLoop850, NameLoop850, Parsed850} from './parser850';
import type {ItemActivityLoop852, NameLoop852, Parsed852} from './parser852';
import type {EdiEnvelopeExplained} from './explainedEnvelope';

export type FindingSeverity = 'error' | 'warning';

export interface ValidationFinding {
  severity: FindingSeverity;
  /** Identifies exactly which segment/element this finding is about, e.g.
   * "groups[0].transactions[0].header.beg" — stable enough for the UI to
   * match a finding back to the field it renders. */
  path: string;
  message: string;
}

function elementValueAsString(
  value: EdiElementValue | undefined
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value.join('') : value;
}

function pad2(position: number): string {
  return String(position).padStart(2, '0');
}

const NUMERIC_PATTERN = /^-?\d+(\.\d+)?$/;

/** True when `value` is all digits of the right length for `format`
 * (YYMMDD, else CCYYMMDD) and names a real calendar day — so "20261301"
 * (month 13) or "20260230" is rejected, not just non-digits. */
function isCalendarDate(value: string, format: string | undefined): boolean {
  const short = format === 'YYMMDD';
  const match = (
    short ? /^(\d{2})(\d{2})(\d{2})$/ : /^(\d{4})(\d{2})(\d{2})$/
  ).exec(value);
  if (!match) return false;
  const year = short ? 2000 + Number(match[1]) : Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/** Checks every dictionary-known element of one segment: required-but-empty,
 * not a member of a fixed code list, or not shaped like its declared
 * date/numeric format. Every check here is advisory — a mismatch against
 * this tool's own (necessarily partial) code lists is reported as a warning,
 * not an error, since the dictionary is generic and may not carry every code
 * a real partner uses. */
function checkElements(
  segment: EdiSegment,
  path: string,
  findings: ValidationFinding[]
): void {
  const definition = getSegmentDefinition(segment.id);

  for (const elementDef of definition.elements) {
    const elementPath = `${path}.elements[${elementDef.position - 1}]`;
    const label = `${segment.id}${pad2(elementDef.position)} (${elementDef.label})`;
    const value = elementValueAsString(
      segment.elements[elementDef.position - 1]
    );

    if (elementDef.required && (value === undefined || value.trim() === '')) {
      findings.push({
        severity: 'error',
        path: elementPath,
        message: `${label} is required but missing.`,
      });
      continue;
    }

    if (value === undefined) continue;
    // Trimmed once and reused by every check below, so a purely
    // whitespace-related formatting difference (incidental surrounding
    // whitespace, or an element that's whitespace-only) never by itself
    // produces a finding; each finding message below still shows the
    // original, untrimmed value as it appeared in the document.
    const trimmedValue = value.trim();
    if (trimmedValue === '') continue;

    if (elementDef.codes && elementDef.codes.length > 0) {
      // ZZ ("Mutually Defined") is a legitimate catch-all by design — its
      // meaning is whatever the trading partners agreed to out of band, so
      // no code-list-checked element anywhere in the dictionary should ever
      // produce an "unrecognized code" finding for it. This is unconditional
      // — it applies regardless of whether this specific field's own code
      // list defines ZZ as "Mutually Defined" (or lists ZZ at all) — rather
      // than only exempting fields whose list happens to already include
      // it. ZZ still renders with its normal label/tooltip wherever a code
      // list does define one for it; only this finding is suppressed.
      //
      // The comparison trims the document value first so a code that's
      // genuinely present in the list isn't misflagged purely because of
      // incidental surrounding whitespace in the source document, and
      // case-folds so a lowercase/mixed-case value (e.g. 'vn') matches the
      // list's 'VN' the same way lib/dictionary/index.ts's getCodeLabel and
      // SegmentField's matched-code lookup do — otherwise a value could
      // render its label (from one of those) while this check still flags
      // it as unrecognized. 'zz'/'Zz' is accepted as the same ZZ exemption
      // for the same reason.
      const normalizedValue = trimmedValue.toUpperCase();
      const known =
        normalizedValue === 'ZZ' ||
        elementDef.codes.some(c => c.code.toUpperCase() === normalizedValue);
      if (!known) {
        findings.push({
          severity: 'warning',
          path: elementPath,
          message: `${label} value "${value}" is not one of this dictionary's known codes.`,
        });
      }
    }

    if (elementDef.dataType === 'date') {
      if (!isCalendarDate(trimmedValue, elementDef.format)) {
        findings.push({
          severity: 'warning',
          path: elementPath,
          message: `${label} value "${value}" doesn't look like a ${elementDef.format ?? 'CCYYMMDD'} date.`,
        });
      }
    }

    if (
      elementDef.dataType === 'numeric' &&
      !NUMERIC_PATTERN.test(trimmedValue)
    ) {
      findings.push({
        severity: 'warning',
        path: elementPath,
        message: `${label} value "${value}" doesn't look like a number.`,
      });
    }
  }
}

interface PathedSegment {
  segment: EdiSegment;
  path: string;
}

function collectNameLoopSegments(
  loop: NameLoop850,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemSegments(item: ItemLoop850, path: string): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.po1, path: `${path}.po1`}];
  item.mea.forEach((s, i) => out.push({segment: s, path: `${path}.mea[${i}]`}));
  item.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  item.pwk.forEach((s, i) => out.push({segment: s, path: `${path}.pwk[${i}]`}));
  item.pkg.forEach((s, i) => out.push({segment: s, path: `${path}.pkg[${i}]`}));
  if (item.po3) out.push({segment: item.po3, path: `${path}.po3`});
  if (item.ctp) out.push({segment: item.ctp, path: `${path}.ctp`});
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (item.per) out.push({segment: item.per, path: `${path}.per`});
  item.sac.forEach((s, i) => out.push({segment: s, path: `${path}.sac[${i}]`}));
  if (item.it8) out.push({segment: item.it8, path: `${path}.it8`});
  if (item.ctb) out.push({segment: item.ctb, path: `${path}.ctb`});
  if (item.tax) out.push({segment: item.tax, path: `${path}.tax`});
  if (item.fob) out.push({segment: item.fob, path: `${path}.fob`});
  item.sdq.forEach((s, i) => out.push({segment: s, path: `${path}.sdq[${i}]`}));
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  if (item.ldt) out.push({segment: item.ldt, path: `${path}.ldt`});
  if (item.iss) out.push({segment: item.iss, path: `${path}.iss`});
  item.pam.forEach((s, i) => out.push({segment: s, path: `${path}.pam[${i}]`}));
  item.msg.forEach((s, i) => out.push({segment: s, path: `${path}.msg[${i}]`}));
  if (item.sln) out.push({segment: item.sln.sln, path: `${path}.sln.sln`});
  item.schedule.forEach((s, i) =>
    out.push({segment: s, path: `${path}.schedule[${i}]`})
  );
  item.csh.forEach((s, i) => out.push({segment: s, path: `${path}.csh[${i}]`}));
  item.tc2.forEach((s, i) => out.push({segment: s, path: `${path}.tc2[${i}]`}));
  item.pct.forEach((s, i) => out.push({segment: s, path: `${path}.pct[${i}]`}));
  if (item.transportation) {
    out.push({
      segment: item.transportation.tc1,
      path: `${path}.transportation.tc1`,
    });
  }
  item.subNameLoops.forEach((sub, i) => {
    const subPath = `${path}.subNameLoops[${i}]`;
    out.push({segment: sub.n1, path: `${subPath}.n1`});
    if (sub.n2) out.push({segment: sub.n2, path: `${subPath}.n2`});
    if (sub.n3) out.push({segment: sub.n3, path: `${subPath}.n3`});
    if (sub.n4) out.push({segment: sub.n4, path: `${subPath}.n4`});
  });
  return out;
}

function collectNameLoopSegments852(
  loop: NameLoop852,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemActivitySegments852(
  item: ItemActivityLoop852,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.lin, path: `${path}.lin`}];
  item.ctp.forEach((s, i) => out.push({segment: s, path: `${path}.ctp[${i}]`}));
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (item.per) out.push({segment: item.per, path: `${path}.per`});
  item.sdq.forEach((s, i) => out.push({segment: s, path: `${path}.sdq[${i}]`}));
  item.qty.forEach((s, i) => out.push({segment: s, path: `${path}.qty[${i}]`}));
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  item.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  item.subNameLoops.forEach((sub, i) => {
    const subPath = `${path}.subNameLoops[${i}]`;
    out.push({segment: sub.n1, path: `${subPath}.n1`});
    if (sub.n2) out.push({segment: sub.n2, path: `${subPath}.n2`});
    if (sub.n3) out.push({segment: sub.n3, path: `${subPath}.n3`});
    if (sub.n4) out.push({segment: sub.n4, path: `${subPath}.n4`});
  });
  return out;
}

function collectParsedSegments852(
  parsed: Parsed852,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.bct, 'header.bct');
  pushAll(header.refs, 'header.refs');
  pushAll(header.dtm, 'header.dtm');
  push(header.per, 'header.per');
  push(header.fob, 'header.fob');
  pushAll(header.pwk, 'header.pwk');
  pushAll(header.msg, 'header.msg');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments852(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(
      ...collectItemActivitySegments852(item, `${basePath}.items[${i}]`)
    );
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

function collectParsedSegments(
  parsed: Parsed850,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.beg, 'header.beg');
  push(header.cur, 'header.cur');
  pushAll(header.refs, 'header.refs');
  push(header.per, 'header.per');
  push(header.tax, 'header.tax');
  push(header.fob, 'header.fob');
  push(header.ctp, 'header.ctp');
  push(header.itd, 'header.itd');
  push(header.dis, 'header.dis');
  push(header.inc, 'header.inc');
  pushAll(header.dtm, 'header.dtm');
  push(header.ldt, 'header.ldt');
  pushAll(header.msg, 'header.msg');
  push(header.iss, 'header.iss');
  push(header.pct, 'header.pct');
  pushAll(header.pwk, 'header.pwk');
  pushAll(header.lm, 'header.lm');
  pushAll(header.lq, 'header.lq');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(...collectNameLoopSegments(loop, `${basePath}.nameLoops[${i}]`));
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemSegments(item, `${basePath}.items[${i}]`));
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

function validateParsed850(
  parsed: Parsed850,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.beg) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.beg`,
      message:
        'Missing BEG segment — every 850 must open with a Beginning Segment for Purchase Order.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message: 'No PO1 line items found in this purchase order.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} PO1 line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function validateParsed852(
  parsed: Parsed852,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.bct) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.bct`,
      message:
        'Missing BCT segment — every 852 must open with a Beginning Segment for Product Transfer and Resale Report.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message: 'No LIN item-activity lines found in this report.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} LIN line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments852(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

/**
 * Validates an envelope (with parsed850/parsed852 attached by
 * lib/explainedEnvelope.ts) and returns a flat, advisory-only list of
 * findings. Never throws, and — deliberately — has no return value or code
 * path that represents "export is blocked".
 */
export function validate(envelope: EdiEnvelopeExplained): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!envelope.isaSegment) {
    findings.push({
      severity: 'error',
      path: 'isaSegment',
      message:
        'Missing ISA segment — this does not look like a complete X12 interchange.',
    });
  }
  if (!envelope.ieaSegment) {
    findings.push({
      severity: 'error',
      path: 'ieaSegment',
      message:
        'Missing IEA segment — the interchange envelope was never closed.',
    });
  }

  envelope.groups.forEach((group, gi) => {
    const groupPath = `groups[${gi}]`;

    if (!group.gsSegment) {
      findings.push({
        severity: 'error',
        path: `${groupPath}.gsSegment`,
        message:
          'A transaction set appears with no enclosing GS functional group.',
      });
    }

    if (!group.geSegment) {
      findings.push({
        severity: 'error',
        path: `${groupPath}.geSegment`,
        message: 'Missing GE segment — this functional group was never closed.',
      });
    } else {
      const declared = elementValueAsString(group.geSegment.elements[0]);
      const actual = group.transactions.length;
      if (declared !== undefined && Number(declared) !== actual) {
        findings.push({
          severity: 'error',
          path: `${groupPath}.geSegment`,
          message: `GE01 says ${declared} transaction set(s) but ${actual} were found.`,
        });
      }
    }

    group.transactions.forEach((transaction, ti) => {
      const txPath = `${groupPath}.transactions[${ti}]`;

      if (
        transaction.transactionSetCode !== '850' &&
        transaction.transactionSetCode !== '852'
      ) {
        findings.push({
          severity: 'warning',
          path: `${txPath}.stSegment`,
          message: `Transaction set type "${transaction.transactionSetCode}" is not an 850 or 852 — shown read-only.`,
        });
      }

      if (!transaction.seSegment) {
        findings.push({
          severity: 'error',
          path: `${txPath}.seSegment`,
          message:
            'Missing SE segment — this transaction set was never closed.',
        });
      } else {
        const declared = elementValueAsString(
          transaction.seSegment.elements[0]
        );
        const actual = transaction.segments.length + 2; // + ST and SE themselves
        if (declared !== undefined && Number(declared) !== actual) {
          findings.push({
            severity: 'error',
            path: `${txPath}.seSegment`,
            message: `SE01 says ${declared} segment(s) but ${actual} were found (including ST and SE).`,
          });
        }
      }

      if (transaction.parsed850) {
        findings.push(...validateParsed850(transaction.parsed850, txPath));
      } else if (transaction.parsed852) {
        findings.push(...validateParsed852(transaction.parsed852, txPath));
      }
    });
  });

  return findings;
}
